import { AbstractControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { CitySearchResult, ContactPerson, WarehouseResult } from '@models';
import { AuthService } from '@services';
import { CardTypes } from '@shared';
import * as moment from 'moment/moment';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith,
  Subscription,
  tap,
} from 'rxjs';
import { AppInjector } from '@app';
import {
  ControlName,
  DeliverySite, RecipientPostMatTooltip, RecipientWarehouseTooltip, SenderPostMatTooltip,
  SenderWarehouseTooltip,
} from '../../components/dashboard/invoices/components/invoices-my/invoices-my.enums';
import { AutocompleteFilterComponent } from '../components/table-filter-menu/components/autocomlete-filter/autocomplete-filter.component';
import { InputFilterComponent } from '../components/table-filter-menu/components/input-filter/input-filter.component';

export const NumberWithTwoDecimalsRegExp = /^[+-]?\d+(\.\d{1,2})?$/;

export function objectIsEmpty(object: object): boolean {
  return !!object && !Object.keys(object).length;
}

export function objectIsNotEmpty(object: object): boolean {
  return !objectIsEmpty(object);
}

export function emptyValue(val: string | number | boolean): boolean {
  return val === null || val === undefined || val === '';
}

export function closeDialog(dialogRef: MatDialogRef<any>) {
  if (dialogRef) {
    dialogRef.close();
  }
}

export function flattenObject(obj: Record<string, any>, depth: number = 1): Record<string, any> {
  let result: Record<string, any> = {};

  for (let key in obj) {
    if (typeof obj[key] === 'object' && obj[key] !== null && depth > 0) {
      if (obj[key] instanceof Date) {
        result[key] = obj[key];
      } else {
        const nestedObject = flattenObject(obj[key], depth - 1);
        Object.assign(result, nestedObject);
      }
    } else {
      result[key] = obj[key];
    }
  }

  return result;
}

export function deleteEmptyValues(object: object): void {
  Object.keys(object).forEach((k) => emptyValue(object[k]) && delete object[k]);
}

export function datesEqual(d1: string, d2: string): boolean {
  const date1 = new Date(d1).setHours(0, 0, 0, 0);
  const date2 = new Date(d2).setHours(0, 0, 0, 0);

  return date1 === date2;
}

export function filterInputValue(
  v: string | null | object,
  minLength = 2,
): boolean {
  return (
    (typeof v === 'string' && (v.length > minLength - 1 || !v.length)) ||
    v === null
  );
}

export function clearField(
  field: AbstractControl,
  event: Event,
  value?: any,
): void {
  event.stopPropagation();
  if (field.reset) {
    if (value === '' || value === null) {
      field.reset(value);

      return;
    }
    field.reset();
  }
}

export function getCounterpartyDescription(
  counterparty: ContactPerson,
): string {
  return counterparty?.Description;
}

export function getCityDescription(city: CitySearchResult): string {
  return city?.Present;
}

export function getWarehouseDescription(warehouse: WarehouseResult): string {
  return warehouse?.Description;
}

export function formatDate(value: string): string {
  return moment(value).format('DD.MM.YYYY');
}

export function senderRefValueChangeSubscription(context: AutocompleteFilterComponent, debounce: number = 500): Subscription {
  return context.control?.valueChanges
    .pipe(
      startWith(''),
      filter((v) => filterInputValue(v)),
      debounceTime(debounce),
    ).subscribe((value) => context.getListSubscription(value).subscribe((counterparties) => {
      context.autocompleteListEmpty = !counterparties.length;
      context.autocompleteList = counterparties || [];
    }));
}

export function recipientRefValueChangeSubscription(context: AutocompleteFilterComponent, debounce: number = 500): Subscription {
  return context.control?.valueChanges
    .pipe(
      pairwise(),
      map(([prev, curr]) => (prev === curr ? false : curr)),
      startWith(''),
      filter(() => !context.form.get(ControlName.RecipientEDRPOU)?.value && context.control?.enabled),
      filter((v) => filterInputValue(v)),
      debounceTime(debounce),
    ).subscribe((value) => context.getListSubscription(value).subscribe((counterparties) => {
      context.autocompleteListEmpty = !counterparties.length;
      context.autocompleteList = counterparties;
    }));
}

export function recipientRefAndEDRPOUSubscription(context: InputFilterComponent): Subscription {
  return combineLatest([
    context.form.get(ControlName.RecipientRef)?.valueChanges.pipe(startWith(context.form.get(ControlName.RecipientRef).value), distinctUntilChanged()),
    context.control?.valueChanges.pipe(startWith(context.control.value), distinctUntilChanged()),
  ]).subscribe(([ref, edrpou]) => {
    const RecipientRef = context.form.get(ControlName.RecipientRef);
    const RecipientEDRPOU = context.control;

    if (edrpou) {
      RecipientRef.reset('');
      RecipientRef.disable();
    } else if (ref) {
      RecipientEDRPOU.reset('');
      RecipientEDRPOU.disable();
    } else {
      if (RecipientRef.disabled) {
        RecipientRef.enable();
      }
      if (RecipientEDRPOU.disabled) {
        RecipientEDRPOU.enable();
      }
    }
  });
}

export function recipientRefTooltip(formGroup: UntypedFormGroup): string {
  return formGroup.get(ControlName.RecipientEDRPOU)?.value ? 'Вже вказано ЄДРПОУ організації' : '';
}

export function recipientEDRPOUTooltip(formGroup: UntypedFormGroup): string {
  return formGroup.get(ControlName.RecipientRef)?.value ? 'Вже обрано організацію з переліку' : '';
}

export function settlementValueChangeSubscription(context: AutocompleteFilterComponent): Subscription {
  return context.control?.valueChanges
    .pipe(
      map((q) => (q === null ? '' : q)),
      tap((query) => {
        if (query.length === 0) {
          context.autocompleteList = [];
        }
      }),
      filter((v) => v.length > 1),
      debounceTime(300),
    ).subscribe((value) => context.getListSubscription(value)
      .subscribe((cities) => (context.autocompleteList = cities.Addresses)));
}

export function senderWarehouseValueChangeSubscription(context: AutocompleteFilterComponent): Subscription {
  const SettlementSender = context.form.get(ControlName.SettlementSender);
  const DeliveryFrom = context.form.get(ControlName.DeliveryFrom);

  return combineLatest([
    SettlementSender?.valueChanges.pipe(startWith(SettlementSender.value)),
    DeliveryFrom?.valueChanges.pipe(startWith(DeliveryFrom.value)),
  ])
    .pipe(startWith([SettlementSender.value, DeliveryFrom.value]))
    .pipe(pairwise())
    .subscribe(([[cityPrev, deliverySitePrev], [city, deliverySite]]) => {
      const DeliveryCity = context.form.get(ControlName.SettlementSender).value?.DeliveryCity;
      const hasWarehouses = !!context.form.get(ControlName.SettlementSender)?.value?.Warehouses;
      const isDeliverySiteWarehouse = deliverySite === DeliverySite.Warehouse;

      if (!DeliveryCity && isDeliverySiteWarehouse) {
        context.autocompleteTooltip = SenderWarehouseTooltip.NoCity;
      }

      if (DeliveryCity && !hasWarehouses && isDeliverySiteWarehouse) {
        context.autocompleteTooltip = SenderWarehouseTooltip.NoWarehouses;
      }

      if (hasWarehouses && isDeliverySiteWarehouse) {
        context.getListSubscription(DeliveryCity)
          .subscribe((warehouses) => {
            context.autocompleteList = warehouses || [];
            context.control[warehouses.length ? 'enable' : 'disable']();
            context.autocompleteTooltip = warehouses.length ? '' : SenderWarehouseTooltip.NoWarehouses;
          });
      }

      if (deliverySite !== deliverySitePrev || cityPrev !== city || !DeliveryCity) {
        context.control.reset('');
        context.control.disable();
        context.autocompleteList = [];
      }
    });
}

export function senderWarehouseDisplayCondition(form: FormGroup): boolean {
  return form.get(ControlName.DeliveryFrom)?.value === DeliverySite.Warehouse;
}

export function senderPostomatsValueChangeSubscription(context: AutocompleteFilterComponent): Subscription {
  const SettlementSender = context.form.get(ControlName.SettlementSender);
  const DeliveryFrom = context.form.get(ControlName.DeliveryFrom);

  return combineLatest([
    SettlementSender?.valueChanges.pipe(startWith(SettlementSender.value)),
    DeliveryFrom?.valueChanges.pipe(startWith(DeliveryFrom.value)),
  ])
    .pipe(startWith([SettlementSender.value, DeliveryFrom.value]))
    .pipe(pairwise())
    .subscribe(([[cityPrev, deliverySitePrev], [city, deliverySite]]) => {
      const DeliveryCity = context.form.get(ControlName.SettlementSender).value?.DeliveryCity;
      const hasWarehouses = !!context.form.get(ControlName.SettlementSender)?.value?.Warehouses;
      const isDeliverySitePostomat = deliverySite === DeliverySite.Postomat;

      if (!DeliveryCity && isDeliverySitePostomat) {
        context.autocompleteTooltip = SenderPostMatTooltip.NoCity;
      }

      if (DeliveryCity && !hasWarehouses && isDeliverySitePostomat) {
        context.autocompleteTooltip = SenderPostMatTooltip.NoWarehouses;
      }

      if (hasWarehouses && isDeliverySitePostomat) {
        context.getListSubscription(DeliveryCity)
          .subscribe((warehouses) => {
            context.autocompleteList = warehouses || [];
            context.control[warehouses.length ? 'enable' : 'disable']();
            context.autocompleteTooltip = warehouses.length ? '' : SenderPostMatTooltip.NoWarehouses;
          });
      }

      if (deliverySite !== deliverySitePrev || cityPrev !== city || !DeliveryCity) {
        context.control.reset('');
        context.control.disable();
        context.autocompleteList = [];
      }
    });
}

export function senderPostomatDisplayCondition(form: FormGroup): boolean {
  return form.get(ControlName.DeliveryFrom)?.value === DeliverySite.Postomat;
}

export function recipientWarehouseValueChangeSubscription(context: AutocompleteFilterComponent): Subscription {
  const SettlementRecipient = context.form.get(ControlName.SettlementRecipient);
  const DeliveryTo = context.form.get(ControlName.DeliveryTo);

  return combineLatest([
    SettlementRecipient?.valueChanges.pipe(startWith(SettlementRecipient.value)),
    DeliveryTo?.valueChanges.pipe(startWith(DeliveryTo.value)),
  ])
    .pipe(startWith([SettlementRecipient.value, DeliveryTo.value]))
    .pipe(pairwise()).subscribe(([[cityPrev, deliverySitePrev], [city, deliverySite]]) => {
      const DeliveryCity = context.form.get(ControlName.SettlementRecipient).value?.DeliveryCity;
      const hasWarehouses = !!context.form.get(ControlName.SettlementRecipient)?.value?.Warehouses;
      const isDeliverySiteWarehouse = deliverySite === DeliverySite.Warehouse;

      if (!DeliveryCity && isDeliverySiteWarehouse) {
        context.autocompleteTooltip = RecipientWarehouseTooltip.NoCity;
      }

      if (DeliveryCity && !hasWarehouses && isDeliverySiteWarehouse) {
        context.autocompleteTooltip = RecipientWarehouseTooltip.NoWarehouses;
      }

      if (hasWarehouses && isDeliverySiteWarehouse) {
        context.getListSubscription(DeliveryCity)
          .subscribe((warehouses) => {
            context.autocompleteList = warehouses || [];
            context.control[warehouses.length ? 'enable' : 'disable']();
            context.autocompleteTooltip = warehouses.length ? '' : RecipientWarehouseTooltip.NoWarehouses;
          });
      }

      if (deliverySite !== deliverySitePrev || cityPrev !== city || !DeliveryCity) {
        context.control.reset('');
        context.control.disable();
        context.autocompleteList = [];
      }
    });
}

export function recipientWarehouseDisplayCondition(form: FormGroup): boolean {
  return form.get(ControlName.DeliveryTo)?.value === DeliverySite.Warehouse;
}

export function recipientPostomatValueChangeSubscription(context: AutocompleteFilterComponent): Subscription {
  const SettlementRecipient = context.form.get(ControlName.SettlementRecipient);
  const DeliveryTo = context.form.get(ControlName.DeliveryTo);

  return combineLatest([
    SettlementRecipient?.valueChanges.pipe(startWith(SettlementRecipient.value)),
    DeliveryTo?.valueChanges.pipe(startWith(DeliveryTo.value)),
  ])
    .pipe(startWith([SettlementRecipient.value, DeliveryTo.value]))
    .pipe(pairwise()).subscribe(([[cityPrev, deliverySitePrev], [city, deliverySite]]) => {
      const DeliveryCity = context.form.get(ControlName.SettlementRecipient).value?.DeliveryCity;
      const hasWarehouses = !!context.form.get(ControlName.SettlementRecipient)?.value?.Warehouses;
      const isDeliverySitePostomat = deliverySite === DeliverySite.Postomat;

      if (!DeliveryCity && isDeliverySitePostomat) {
        context.autocompleteTooltip = RecipientPostMatTooltip.NoCity;
      }

      if (DeliveryCity && !hasWarehouses && isDeliverySitePostomat) {
        context.autocompleteTooltip = RecipientPostMatTooltip.NoWarehouses;
      }

      if (hasWarehouses && isDeliverySitePostomat) {
        context.getListSubscription(DeliveryCity)
          .subscribe((warehouses) => {
            context.autocompleteList = warehouses || [];
            context.control[warehouses.length ? 'enable' : 'disable']();
            context.autocompleteTooltip = warehouses.length ? '' : RecipientWarehouseTooltip.NoWarehouses;
          });
      }

      if (deliverySite !== deliverySitePrev || cityPrev !== city || !DeliveryCity) {
        context.control.reset('');
        context.control.disable();
        context.autocompleteList = [];
      }
    });
}

export function recipientPostomatDisplayCondition(form: FormGroup): boolean {
  return form.get(ControlName.DeliveryTo)?.value === DeliverySite.Postomat;
}

export function recipientWarehouseFilterQuery(form: UntypedFormGroup) {
  return form.get(ControlName.RecipientWarehouse)?.valueChanges.pipe(
    filter((v) => filterInputValue(v, 1)),
    debounceTime(300));
}

export function senderWarehouseFilterQuery(form: UntypedFormGroup) {
  return form.get(ControlName.SenderWarehouse)?.valueChanges.pipe(
    filter((v) => filterInputValue(v, 1)),
    debounceTime(300));
}

export function afterpaymentDisplayCondition(): boolean {
  const { user } = AppInjector.get(AuthService);

  const userIsRozniza = user?.loyaltyCardType === CardTypes.ROZNIZA;
  const userIsCcard = user?.loyaltyCardType === CardTypes.CCARD;
  return !(userIsRozniza || userIsCcard);
}

export function senderRefDisplayCondition(): boolean {
  const { user } = AppInjector.get(AuthService);

  const userIsRozniza = user?.loyaltyCardType === CardTypes.ROZNIZA;
  return !userIsRozniza;
}

export function additionalServiceDisplayCondition(): boolean {
  const { user } = AppInjector.get(AuthService);

  const userIsCcard = user?.loyaltyCardType === CardTypes.CCARD;
  const userIsIdentification = user?.loyaltyCardType === CardTypes.IDENTIFICATION;
  const userIsCorporate = user?.type === 'org';
  return userIsIdentification || userIsCorporate || userIsCcard;
}
